import React, { useState, useEffect } from 'react';
import { Building2, X, Download } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from '../config/api';

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [settings, setSettings] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [waExpanded, setWaExpanded] = useState(false);

  // Listen for WhatsApp FAB expanded state so the two mobile popups never overlap.
  useEffect(() => {
    const h = (e) => setWaExpanded(Boolean(e.detail?.open));
    window.addEventListener('osh:wa-expanded', h);
    return () => window.removeEventListener('osh:wa-expanded', h);
  }, []);

  // Broadcast our open state so WhatsApp FAB can collapse to the small icon
  // while the install prompt is on-screen.
  useEffect(() => {
    const open = showPrompt && !!settings;
    window.dispatchEvent(new CustomEvent('osh:pwa-prompt', { detail: { open } }));
  }, [showPrompt, settings]);

  useEffect(() => {
    // Check if already dismissed in this session
    const isDismissed = sessionStorage.getItem('pwa_prompt_dismissed');
    if (isDismissed) {
      setDismissed(true);
      return;
    }

    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      return;
    }

    // Fetch PWA settings
    fetchPWASettings();

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  useEffect(() => {
    // Show prompt if we have settings, prompt is enabled, and browser supports it
    if (settings?.enabled && settings?.install_prompt_enabled && deferredPrompt && !dismissed) {
      // Delay showing the prompt for better UX
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [settings, deferredPrompt, dismissed]);

  const fetchPWASettings = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/pwa/settings`);
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch PWA settings');
    }
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowPrompt(false);
    }
    
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    sessionStorage.setItem('pwa_prompt_dismissed', 'true');
  };

  if (!showPrompt || !settings) return null;
  // If the WhatsApp support popup is expanded, step aside so it isn't covered.
  if (waExpanded) return null;

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 bg-white rounded-xl shadow-2xl border border-slate-200 p-4 z-50 animate-in slide-in-from-bottom duration-300"
      data-testid="pwa-install-prompt"
    >
      <button
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-600 transition-colors"
        data-testid="dismiss-pwa-prompt"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="flex items-start gap-4">
        <div 
          className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: settings.theme_color || '#F97316' }}
        >
          <Building2 className="h-7 w-7 text-white" />
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-slate-900 text-lg">
            {settings.install_prompt_title || 'Install OnsiteHand'}
          </h3>
          <p className="text-slate-600 text-sm mt-1">
            {settings.install_prompt_description || 'Add to home screen for quick access'}
          </p>
          
          <div className="flex items-center gap-3 mt-4">
            <button
              onClick={handleInstall}
              className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
              style={{ backgroundColor: settings.theme_color || '#F97316' }}
              data-testid="install-pwa-btn"
            >
              <Download className="h-4 w-4" />
              {settings.install_prompt_button_text || 'Install App'}
            </button>
            <button
              onClick={handleDismiss}
              className="text-slate-500 hover:text-slate-700 text-sm font-medium"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
